import { useState } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence, motion } from 'framer-motion';
import { PackageCheck } from 'lucide-react';

import { Collapsible } from 'components/Collapsible';
import { CompletedGroupHeader } from 'components/Kit/EmailSuggestions/components/CompletedGroupHeader';
import {
  KIT_ANIMATIONS,
  SUGGESTIONS_PAGE_SIZE,
} from 'components/Kit/EmailSuggestions/constants';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';

import { BUILT_GROUP_EXPLANATION, BUILT_GROUP_LABEL } from '../constants';
import { BuiltLoadRow } from './BuiltLoadRow';

type BuiltLoadsGroupProps = {
  suggestions: LoadBuildingSuggestions[];
  onOpenLoad: (loadIdentifier: string) => void;
};

// Load building cards whose load already exists, shown with the handled quote groups behind Kit's
// "View built loads" toggle. Collapsed by default. Shows the first page of rows, then "Show N more".
export function BuiltLoadsGroup({
  suggestions,
  onOpenLoad,
}: BuiltLoadsGroupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(SUGGESTIONS_PAGE_SIZE);

  if (suggestions.length === 0) {
    return null;
  }

  const hiddenCount = Math.max(suggestions.length - visibleCount, 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className='w-full'>
      <CompletedGroupHeader
        icon={<PackageCheck className='h-3.5 w-3.5 shrink-0 text-info-700' />}
        label={BUILT_GROUP_LABEL}
        count={suggestions.length}
        explanation={BUILT_GROUP_EXPLANATION}
        colorClass='border-info-300 bg-info-50 hover:bg-info-100'
      />
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key='built-loads-group'
            {...KIT_ANIMATIONS.toggle}
            className='mb-2 overflow-hidden'
          >
            <Flex
              direction='col'
              gap='xs'
              className='mt-1.5 ml-3 w-[calc(100%-12px)] border-l border-info-300 pl-3'
            >
              {suggestions.slice(0, visibleCount).map((suggestion, index) => (
                <BuiltLoadRow
                  key={suggestion.id}
                  suggestion={suggestion}
                  ordinal={`${index + 1}.`}
                  onOpenLoad={onOpenLoad}
                />
              ))}
              {hiddenCount > 0 && (
                <button
                  type='button'
                  onClick={() =>
                    setVisibleCount((count) => count + SUGGESTIONS_PAGE_SIZE)
                  }
                  className='w-fit border-none bg-transparent px-1 text-accent-600 underline cursor-pointer'
                >
                  <Typography
                    as='span'
                    variant='body-xxs'
                    className='text-accent-600'
                  >
                    {`Show ${hiddenCount} more`}
                  </Typography>
                </button>
              )}
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </Collapsible>
  );
}
